/**
 * Sender avatar utilities: company favicon lookup, deterministic initials, and background color.
 *
 * Architecture overview
 * ─────────────────────
 * 1. URL resolution (getSenderAvatarOptions / resolveAvatarUrl)
 *    Deterministic: explicit prop > Contact entity avatarUrl > company favicon > Gravatar proxy.
 *
 * 2. Session URL result cache (_knownGoodUrls / _knownFailedUrls)
 *    Tracks which URLs succeeded or failed THIS session.
 *    SenderAvatar skips the semaphore entirely for known-good URLs — browser cache is warm,
 *    load will be near-instant.  Known-failed URLs short-circuit to initials immediately.
 *
 * 3. Background preloader (preloadAvatarUrls)
 *    Called whenever a new batch of threads arrives (MailApp.tsx useEffect).
 *    Fires parallel Image() preloads with a modest concurrency cap so that by the time
 *    SenderAvatar components mount, most URLs are already in the browser cache and
 *    the result cache. Components that mount after preloading skip the queue entirely.
 *
 * 4. Reactive Contact avatar cache (useContactAvatarStore)
 *    Zustand store mirroring _contactAvatarCache.  SenderAvatar subscribes per-email so
 *    it re-renders automatically when a Contact entity's Gravatar URL is enriched in the
 *    background.
 */

import CryptoJS from 'crypto-js';
import { create } from 'zustand';
import { API_BASE_URL } from '../../../api/config';

// ── Session URL result cache ──────────────────────────────────────────────────
// Populated by the background preloader and by SenderAvatar's onLoad/onError.
// Keyed by the resolved URL string.

const _knownGoodUrls = new Set<string>();
const _knownFailedUrls = new Set<string>();

export type AvatarUrlStatus = 'good' | 'failed' | 'unknown';

export function getAvatarUrlStatus(url: string): AvatarUrlStatus {
  if (_knownGoodUrls.has(url)) return 'good';
  if (_knownFailedUrls.has(url)) return 'failed';
  return 'unknown';
}

export function markAvatarUrlGood(url: string): void {
  _knownGoodUrls.add(url);
  _knownFailedUrls.delete(url);
}

export function markAvatarUrlFailed(url: string): void {
  _knownFailedUrls.add(url);
  _knownGoodUrls.delete(url);
}

// ── Reactive Contact avatar cache ─────────────────────────────────────────────
// Module-level Map for O(1) synchronous reads; Zustand store for React reactivity.

const _contactAvatarCache = new Map<string, string>();

interface AvatarCacheState {
  /** email (lowercase) → avatarUrl */
  urls: Record<string, string>;
}

export const useContactAvatarStore = create<AvatarCacheState>(() => ({
  urls: {},
}));

/** Sync the cache from the current set of Contact entities in the entity store. */
export function populateContactAvatarCache(
  contacts: Array<{ skeleton: { email?: string; avatarUrl?: string } }>
): void {
  const newEntries: Record<string, string> = {};
  for (const c of contacts) {
    const email = c.skeleton.email?.trim().toLowerCase();
    const url = c.skeleton.avatarUrl?.trim();
    if (!email) continue;
    if (url && _contactAvatarCache.get(email) !== url) {
      _contactAvatarCache.set(email, url);
      newEntries[email] = url;
    }
  }
  const added = Object.keys(newEntries).length;
  if (added > 0) {
    useContactAvatarStore.setState((s) => ({ urls: { ...s.urls, ...newEntries } }));
  }
}

/** Look up a stored Contact avatar URL by email (O(1)). Returns null if not found. */
export function getStoredContactAvatarUrl(email: string): string | null {
  return _contactAvatarCache.get(email.trim().toLowerCase()) ?? null;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SenderAvatarInput {
  name?: string;
  email: string;
  imageUrl?: string;
}

export interface SenderAvatarOptions {
  imageUrl: string | null;
  fallbackImageUrls: string[];
  initials: string;
  color: string;
}

// ── Personal vs company domain list ──────────────────────────────────────────

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.fr', 'yahoo.de', 'ymail.com',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de',
  'outlook.com', 'outlook.fr', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me',
  'fastmail.com', 'fastmail.fm',
  'zoho.com',
  'mail.com', 'gmx.com', 'gmx.net',
  'inbox.com',
  'yandex.com', 'yandex.ru',
  'tutanota.com', 'tuta.io',
]);

function md5(str: string): string {
  return CryptoJS.MD5(str).toString();
}

/**
 * Get 1–2 letter initials from name and/or email.
 * Prefers name: "First Last" -> "FL", "First" -> "F", "first.last@domain.com" -> "FL".
 * Fallback: first character of email local part, or "?".
 */
export function getInitials(name?: string, email?: string): string {
  const trimmedName = name?.trim();
  if (trimmedName) {
    const parts = trimmedName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const first = parts[0].charAt(0);
      const last = parts[parts.length - 1].charAt(0);
      if (first && last) return (first + last).toUpperCase();
    }
    if (parts[0]) return parts[0].charAt(0).toUpperCase();
  }
  if (email) {
    const local = email.split('@')[0]?.trim() || '';
    if (local.includes('.')) {
      const segs = local.split('.');
      const a = segs[0]?.charAt(0);
      const b = segs[segs.length - 1]?.charAt(0);
      if (a && b) return (a + b).toUpperCase();
    }
    if (local) return local.charAt(0).toUpperCase();
  }
  return '?';
}

/**
 * Deterministic HSL background color from email (stable per sender).
 */
export function getAvatarColor(email: string): string {
  if (!email?.trim()) return 'hsl(0, 0%, 45%)';
  const hash = md5(email.trim().toLowerCase());
  const hue = parseInt(hash.slice(0, 8), 16) % 360;
  return `hsl(${hue}, 55%, 42%)`;
}

// Known two-part TLD second levels (e.g. co.uk, com.au) so we take 3 parts instead of 2.
const MULTI_PART_TLDS = new Set([
  'co.uk', 'co.jp', 'co.nz', 'co.za', 'co.in', 'co.kr', 'co.il', 'co.id',
  'com.au', 'com.br', 'com.mx', 'com.sg', 'com.ar', 'com.pe', 'com.co', 'com.ng',
  'org.uk', 'net.uk', 'gov.uk', 'ac.uk', 'me.uk',
  'ne.jp', 'or.jp', 'go.jp', 'ac.jp',
]);

/**
 * Extracts the registrable root domain (eTLD+1) from a full domain.
 * e.g. "rivereditor.on.crisp.email" → "crisp.email"
 *      "mail.stripe.com"            → "stripe.com"
 *      "foo.bar.co.uk"              → "bar.co.uk"
 */
function getRootDomain(domain: string): string {
  const parts = domain.split('.');
  if (parts.length <= 2) return domain;
  const twoPartTld = parts.slice(-2).join('.');
  if (MULTI_PART_TLDS.has(twoPartTld)) {
    return parts.slice(-3).join('.');
  }
  return parts.slice(-2).join('.');
}

/**
 * Returns the email domain if it's a genuine company domain suitable for a favicon lookup.
 * Returns null for personal email providers.
 */
export function getCompanyDomain(email: string): string | null {
  const domain = email.split('@')[1]?.toLowerCase().trim();
  if (!domain) return null;
  if (PERSONAL_EMAIL_DOMAINS.has(domain)) return null;
  return domain;
}

/**
 * Returns direct favicon image candidates for company senders, using the root domain so
 * subdomains like "rivereditor.on.crisp.email" resolve to "crisp.email".
 *
 * These deliberately do not use the backend proxy: <img> can load cross-origin
 * images directly, and avoiding the backend removes an entire bottleneck/failure
 * mode from the inbox render path.
 * Returns null for personal email providers.
 */
export function getCompanyFaviconUrl(email: string, size: number = 64): string | null {
  const domain = getCompanyDomain(email);
  if (!domain) return null;
  const rootDomain = getRootDomain(domain);
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(rootDomain)}&sz=${size}`;
}

export function getCompanyFaviconFallbackUrls(email: string): string[] {
  const domain = getCompanyDomain(email);
  if (!domain) return [];
  const rootDomain = getRootDomain(domain);
  return [`https://icons.duckduckgo.com/ip3/${encodeURIComponent(rootDomain)}.ico`];
}

/**
 * Returns the backend-proxied Gravatar URL for personal email senders.
 * The backend fetches Gravatar server-side and returns 204 if no photo exists,
 * so the browser never sees a 404.
 */
export function getPersonalAvatarUrl(email: string): string {
  return `${API_BASE_URL}/mail/sender-avatar?email=${encodeURIComponent(email)}`;
}

/**
 * Resolve only the avatar URL for an email (no initials/color computation).
 * Used by the preloader — avoids the overhead of getSenderAvatarOptions.
 */
export function resolveAvatarUrl(email: string): string | null {
  const normalizedEmail = email?.trim() || '';
  if (!normalizedEmail) return null;
  return (
    getStoredContactAvatarUrl(normalizedEmail) ??
    getCompanyFaviconUrl(normalizedEmail) ??
    getPersonalAvatarUrl(normalizedEmail)
  );
}

export function resolveAvatarUrlCandidates(email: string): string[] {
  const normalizedEmail = email?.trim() || '';
  if (!normalizedEmail) return [];

  const contactUrl = getStoredContactAvatarUrl(normalizedEmail);
  if (contactUrl) return [contactUrl];

  const companyUrl = getCompanyFaviconUrl(normalizedEmail);
  if (companyUrl) {
    return [companyUrl, ...getCompanyFaviconFallbackUrls(normalizedEmail)];
  }

  return [getPersonalAvatarUrl(normalizedEmail)];
}

/**
 * Full options for rendering a sender avatar:
 *   explicit imageUrl
 *   > stored Contact avatarUrl (populated from Gravatar during contact discovery)
 *   > company favicon (company domains)
 *   > proxied Gravatar (personal domains, for emails not yet in contacts)
 *   > initials + color
 */
export function getSenderAvatarOptions(input: SenderAvatarInput): SenderAvatarOptions {
  const { name, email, imageUrl } = input;
  const normalizedEmail = email?.trim() || '';
  const initials = getInitials(name, normalizedEmail);
  const color = getAvatarColor(normalizedEmail);

  let url: string | null = imageUrl?.trim() || null;
  let fallbackImageUrls: string[] = [];
  if (!url && normalizedEmail) {
    const candidates = resolveAvatarUrlCandidates(normalizedEmail);
    url = candidates[0] ?? null;
    fallbackImageUrls = candidates.slice(1);
  }

  return { imageUrl: url || null, fallbackImageUrls, initials, color };
}

// ── Background preloader ──────────────────────────────────────────────────────
// Warms the browser cache and the session result cache before SenderAvatar
// components even try to load images.  Components that mount AFTER preloading
// skip the semaphore entirely and show images with no queue delay.
//
// Each unique URL is only ever preloaded once per session — subsequent calls
// with the same emails are no-ops.

const _preloadedUrls = new Set<string>();

/**
 * Preload avatar images for a list of email addresses in the background.
 *
 * @param emails  - Sender email addresses (duplicates are fine, deduplicated internally).
 * @param concurrency - Max parallel Image() loads (default 10).
 */
export function preloadAvatarUrls(emails: string[], concurrency = 10): void {
  // Derive unique URLs that haven't been preloaded or resolved yet.
  const toLoad: string[] = [];
  const seen = new Set<string>();
  for (const email of emails) {
    // Only preload the primary URL. Fallback candidates are tried on demand
    // when the primary source fails or returns a tiny placeholder.
    const url = resolveAvatarUrl(email);
    if (!url) continue;
    if (seen.has(url)) continue;
    seen.add(url);
    if (_preloadedUrls.has(url) || _knownGoodUrls.has(url) || _knownFailedUrls.has(url)) continue;
    _preloadedUrls.add(url);
    toLoad.push(url);
  }

  if (toLoad.length === 0) return;

  let idx = 0;
  let active = 0;

  function next(): void {
    while (active < concurrency && idx < toLoad.length) {
      const url = toLoad[idx++];
      active++;
      const img = new Image();
      img.onload = () => {
        active--;
        if (img.naturalWidth > 16) {
          markAvatarUrlGood(url);
        } else {
          // Fallback globe / blank — backend returned 204 equivalent
          markAvatarUrlFailed(url);
        }
        next();
      };
      img.onerror = () => {
        active--;
        markAvatarUrlFailed(url);
        next();
      };
      img.src = url;
    }

  }

  next();
}
